import { createSettingsParams, ISettingsParam, SettingsParamType } from '@wix/tpa-settings';
import { Alignments } from './Settings/Components/Alignment/consts';
import { SPECS } from '../../consts';
import { SettingsParamsKeys } from './common/settingsAndStylesEnums';
import {DataType} from '@wix/editor-platform-ooi-metadata-resolver';

export type ISettingsParams = {
  [SettingsParamsKeys.descriptionLimitRows]: SettingsParamType.Number;
  [SettingsParamsKeys.deliveryDate]: SettingsParamType.Boolean;
  [SettingsParamsKeys.quantity]: SettingsParamType.Boolean;
  [SettingsParamsKeys.productTitleAlignment]: SettingsParamType.String;
  [SettingsParamsKeys.descriptionAlignment]: SettingsParamType.String;
  [SettingsParamsKeys.buyNowButtonText]: SettingsParamType.Text;
  [SettingsParamsKeys.showBuyNowButton]: SettingsParamType.Boolean;
  [SettingsParamsKeys.showAddToCartButton]: SettingsParamType.Boolean;
  [SettingsParamsKeys.addToCartButtonText]: SettingsParamType.Text;
  [SettingsParamsKeys.amountLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.recipientEmailLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.enterCustomAmountLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.otherAmountVariantLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.deliveryDateLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.deliveryDateDefaultText]: SettingsParamType.Text;
  [SettingsParamsKeys.recipientNameLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.giftMessageLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.quantityLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.descriptionReadMoreButton]: SettingsParamType.Text;
  [SettingsParamsKeys.descriptionReadLessButton]: SettingsParamType.Text;
  [SettingsParamsKeys.amountErrorMessage]: SettingsParamType.Text;
  [SettingsParamsKeys.recipientEmailErrorMessage]: SettingsParamType.Text;
  [SettingsParamsKeys.srDeliveryDateChooseDateButton]: SettingsParamType.Text;
  [SettingsParamsKeys.srQuantityRemoveQuantity]: SettingsParamType.Text;
  [SettingsParamsKeys.srQuantityTotalQuantity]: SettingsParamType.Text;
  [SettingsParamsKeys.increaseQuantityAriaLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.decreaseQuantityAriaLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.srQuantityAddQuantity]: SettingsParamType.Text;
  [SettingsParamsKeys.quantityErrorMessage]: SettingsParamType.Text;
  [SettingsParamsKeys.giftCardForLabel]: SettingsParamType.Text;
  [SettingsParamsKeys.someoneElseButton]: SettingsParamType.Text;
  [SettingsParamsKeys.myselfButton]: SettingsParamType.Text;
  [SettingsParamsKeys.giftCardFor]: SettingsParamType.Text;
  [SettingsParamsKeys.addToCartPostAction]: SettingsParamType.Text;
  [SettingsParamsKeys.displayTitle]: SettingsParamType.Boolean;
  [SettingsParamsKeys.displayDescription]: SettingsParamType.Boolean;
  [SettingsParamsKeys.displayAmountOptions]: SettingsParamType.Boolean;
  [SettingsParamsKeys.displayQuantitySelector]: SettingsParamType.Boolean;
  [SettingsParamsKeys.displayImage]: SettingsParamType.Boolean;
};

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

const settingsParams = createSettingsParams<ISettingsParams>({
  [SettingsParamsKeys.descriptionLimitRows]: {
    type: SettingsParamType.Number,
    getDefaultValue: () => 3,
  },
  [SettingsParamsKeys.productTitleAlignment]: {
    type: SettingsParamType.String,
    getDefaultValue: () => Alignments.LEFT,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.LEFT,
      },
    },
  },
  [SettingsParamsKeys.descriptionAlignment]: {
    type: SettingsParamType.String,
    getDefaultValue: () => Alignments.LEFT,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.LEFT,
      },
    },
  },
  [SettingsParamsKeys.deliveryDate]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.quantity]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.showBuyNowButton]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.showAddToCartButton]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => false,
  },
  [SettingsParamsKeys.buyNowButtonText]: textSettingsParam(
    'settings.giftCardPage.textTab.buyNow.defaultText'
  ),
  [SettingsParamsKeys.addToCartButtonText]: textSettingsParam(
    'settings.giftCardPage.textTab.addToCart.defaultText'
  ),
  [SettingsParamsKeys.amountLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.amountOption.defaultText'
  ),
  [SettingsParamsKeys.recipientEmailLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.email.defaultText'
  ),
  [SettingsParamsKeys.enterCustomAmountLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.enterCustomAmount.defaultText'
  ),
  [SettingsParamsKeys.otherAmountVariantLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.customAmount.defaultText'
  ),
  [SettingsParamsKeys.deliveryDateLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.date.title'
  ),
  [SettingsParamsKeys.deliveryDateDefaultText]: textSettingsParam(
    'giftCardPage.deliveryDate.defaultText'
  ),
  [SettingsParamsKeys.recipientNameLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.name.defaultText'
  ),
  [SettingsParamsKeys.giftMessageLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.customMessage.defaultText'
  ),
  [SettingsParamsKeys.quantityLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.quantity.defaultText'
  ),
  [SettingsParamsKeys.descriptionReadMoreButton]: textSettingsParam(
    'giftCardPage.description.readMore.button'
  ),
  [SettingsParamsKeys.descriptionReadLessButton]: textSettingsParam(
    'giftCardPage.description.readLess.button'
  ),
  [SettingsParamsKeys.amountErrorMessage]: textSettingsParam(
    'giftCardPage.amount.errorMessage'
  ),
  [SettingsParamsKeys.recipientEmailErrorMessage]: textSettingsParam(
    'giftCardPage.recipientEmail.errorMessage'
  ),
  [SettingsParamsKeys.srDeliveryDateChooseDateButton]: textSettingsParam(
    'giftCardPage.sr.deliveryDate.chooseDate.button'
  ),
  [SettingsParamsKeys.srQuantityRemoveQuantity]: textSettingsParam(
    'giftCardPage.sr.quantity.removeQuantity'
  ),
  [SettingsParamsKeys.srQuantityTotalQuantity]: textSettingsParam(
    'giftCardPage.sr.quantity.totalQuantity'
  ),
  [SettingsParamsKeys.increaseQuantityAriaLabel]: textSettingsParam(
    'giftCards.increaseQuantity.ariaLabel'
  ),
  [SettingsParamsKeys.decreaseQuantityAriaLabel]: textSettingsParam(
    'giftCards.decreaseQuantity.ariaLabel'
  ),
  [SettingsParamsKeys.srQuantityAddQuantity]: textSettingsParam(
    'giftCardPage.sr.quantity.addQuantity'
  ),
  [SettingsParamsKeys.quantityErrorMessage]: textSettingsParam(
    'giftCardPage.quantity.errorMessage'
  ),
  [SettingsParamsKeys.giftCardForLabel]: textSettingsParam(
    'settings.giftCardPage.textTab.recipient.defaultText'
  ),
  [SettingsParamsKeys.someoneElseButton]: textSettingsParam(
    'settings.giftCardPage.textTab.forSomeoneElse.defaultText'
  ),
  [SettingsParamsKeys.myselfButton]: textSettingsParam(
    'settings.giftCardPage.textTab.forMyself.defaultText'
  ),
  [SettingsParamsKeys.giftCardFor]: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) => `both`,
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: `both`,
      },
    }),
  },
  [SettingsParamsKeys.addToCartPostAction]: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) => `openSideCart`,
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: `openSideCart`,
      },
    }),
  },
  [SettingsParamsKeys.displayTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.displayDescription]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.displayAmountOptions]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.displayQuantitySelector]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  [SettingsParamsKeys.displayImage]: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
});

export default settingsParams;
